import React from 'react';

import {
  required,
  TextInput,
} from 'react-admin';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogContent,
  Typography,
} from '@mui/material';

import { useWatch } from 'react-hook-form';

import BooleanRadioGroupInput from '../../../../customFields/BooleanRadioGroupInput';
import { PendingReview } from '../../../../hooks/usePendingReview';

export type ActionFormContentProps = {
  requirePeerReview: boolean;
  isSubmitting: boolean;
  pendingReview: PendingReview | null | undefined;
  pendingReviewLoading: boolean;
  closeDialog: () => void;
  isPublish?: boolean;
};

const ActionFormContent = ({
  requirePeerReview,
  isSubmitting,
  pendingReview,
  pendingReviewLoading,
  closeDialog,
  isPublish,
}: ActionFormContentProps) => {
  const isTestVersion = useWatch({ name: 'isTestVersion' });

  // For publish: requirePeerReview only applies to non-test versions
  // For release: requirePeerReview always applies (no test version choice)
  const needsReview = isPublish
    ? requirePeerReview && !isTestVersion
    : requirePeerReview;

  const isBlocked = needsReview && !!pendingReview;
  const shouldDisableSubmit = isSubmitting || isBlocked || (needsReview && pendingReviewLoading);

  const actionLabel = isPublish ? 'Publish' : 'Release';

  const getSubmitLabel = () => {
    if (isSubmitting) {
      if (needsReview) return 'Submitting...';
      return isPublish ? 'Publishing...' : 'Releasing...';
    }
    if (needsReview) return 'Submit for review';
    return actionLabel;
  };

  return (
    <DialogContent>
      <Box display="flex" flexDirection="column" gap={4}>
        {!isPublish && (
          <Typography>The current test version will be released as a live version.</Typography>
        )}

        {isPublish && (
          <BooleanRadioGroupInput
            source="isTestVersion"
            label="Should the rule tree be published as a test version?"
            defaultValue={false}
            validate={[required()]}
            helperText={false}
          />
        )}

        <TextInput
          multiline
          rows={4}
          source="description"
          fullWidth
          label="Description"
          validate={[required()]}
          helperText={false}
        />

        {needsReview && !isBlocked && (
          <TextInput
            multiline
            rows={4}
            source="reasoning"
            fullWidth
            label="Reasoning"
            validate={[required()]}
            helperText={false}
          />
        )}

        {isBlocked && (
          <Alert severity="warning">
            A review for this action is already pending. You cannot submit another review
            until the current one is resolved.
          </Alert>
        )}

        <Box gap={2} display="flex" justifyContent="flex-end">
          <Button
            onClick={closeDialog}
            color="error"
            variant="contained"
            disabled={isSubmitting}
          >
            Close
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={shouldDisableSubmit}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {getSubmitLabel()}
          </Button>
        </Box>
      </Box>
    </DialogContent>
  );
};

export default ActionFormContent;
